/* Bibliothèque de briefs PDF — archive filtrable de tous les briefs générés.
 *
 * Sources :
 *  - Impact Radar  (source_module='radar')
 *  - autres briefs futurs (Executive Brief, etc.)
 *
 * Le PDF n'est PAS stocké : on régénère à la volée via l'URL fournie par
 * /api/workspace/briefs/library/{id}/regenerate-url.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import {
  FileText, FileDown, Loader2, Filter, Crown, TrendingUp, TrendingDown, Activity,
  Trash2, Library, ExternalLink, MessageSquare,
} from "lucide-react";

const DIRECTION_META = {
  opportunite: { label: "Opportunité", color: "#1D9E75", Icon: TrendingUp },
  menace:      { label: "Menace",      color: "#EF4444", Icon: TrendingDown },
  mixte:       { label: "Mixte",       color: "#EF9F27", Icon: Activity },
};

export default function BriefsLibrary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    direction: "",
    horizon: "",
    source_module: "",
    min_score: "",
    days: "",
  });
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      params.limit = 200;
      const r = await api.get("/workspace/briefs/library", { params });
      setData(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Chargement bibliothèque impossible."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.direction, filters.horizon, filters.source_module, filters.min_score, filters.days]);

  const download = async (b) => {
    setDownloadingId(b.id);
    try {
      const u = await api.get(`/workspace/briefs/library/${b.id}/regenerate-url`);
      const path = u.data?.url;
      if (!path) throw new Error("URL de régénération introuvable.");
      // Call backend to regenerate PDF
      const r = await api.post(path.replace("/api", ""), null, { responseType: "blob", timeout: 60000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `brief-${b.id.slice(0, 8)}-${(b.created_at || "").slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify.success("Brief régénéré et téléchargé.");
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Téléchargement impossible."));
    } finally { setDownloadingId(null); }
  };

  const remove = async (b) => {
    if (!confirm(`Retirer "${b.title}" de la bibliothèque ?`)) return;
    try {
      await api.delete(`/workspace/briefs/library/${b.id}`);
      await load();
      notify.success("Retiré.");
    } catch (e) {
      notify.error(fmtError(e, "Suppression impossible."));
    }
  };

  // V23.15.25 — Fix P1 : rouvrir la conversation Intelligence V2 archivée.
  // On écrit le session_id du brief dans localStorage (même clé que IntelligenceV2.jsx
  // V23.15.21) et on redirige. Le composant chat rechargera automatiquement l'historique
  // via GET /sessions/{id}/messages.
  const reopenConversation = (b) => {
    const sessionId = b?.extra?.session_id;
    if (!sessionId) {
      notify.error("Cette conversation ne peut pas être rouverte (session_id manquant — brief antérieur au fix V23.15.25).");
      return;
    }
    try {
      localStorage.setItem(
        "noam_intel_v2_session",
        JSON.stringify({ id: sessionId, created_at: Date.now() })
      );
    } catch {
      // quota plein → on continue quand même, le composant fallbackra
    }
    notify.success("Conversation rouverte.");
    navigate("/app/intelligence");
  };

  const filtered = useMemo(() => data?.items || [], [data]);

  return (
    <div className="space-y-5" data-testid="briefs-library-page">
      <ThreeQPageHeader endpoint="/workspace/briefs-library/top-priority" />
      <div>
        <div className="flex items-center gap-2 noam-overline text-[#EF9F27]">
          <Library className="w-3 h-3" /> Archive comité
        </div>
        <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">Bibliothèque de briefs</h1>
        <p className="text-zinc-400 text-sm mt-1 max-w-2xl leading-relaxed">
          Tous les briefs PDF générés dans Noam — filtrez par score, direction, horizon, module source. Les PDF sont
          régénérés à la volée pour toujours refléter les dernières données.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Briefs total" value={data?.total_all || 0} color="#378ADD" />
        <KPI label="Ce mois (30j)" value={data?.last_30_days || 0} color="#EF9F27" />
        <KPI label="Filtres actifs" value={filtered.length} color="#1D9E75" />
      </div>

      <div className="noam-card p-3 flex items-end gap-2 flex-wrap" data-testid="briefs-filters">
        <Filter className="w-4 h-4 text-zinc-500 mb-2" />
        <SelectField label="Module" value={filters.source_module} onChange={(v) => setFilters({ ...filters, source_module: v })} options={[["", "Tous"], ["radar", "Radar"]]} testid="briefs-filter-module" />
        <SelectField label="Direction" value={filters.direction} onChange={(v) => setFilters({ ...filters, direction: v })} options={[["", "Toutes"], ["opportunite", "Opportunité"], ["menace", "Menace"], ["mixte", "Mixte"]]} testid="briefs-filter-direction" />
        <SelectField label="Horizon" value={filters.horizon} onChange={(v) => setFilters({ ...filters, horizon: v })} options={[["", "Tous"], ["immediat", "Immédiat"], ["court_terme", "Court terme"], ["moyen_terme", "Moyen terme"], ["long_terme", "Long terme"]]} testid="briefs-filter-horizon" />
        <NumField label="Score ≥" value={filters.min_score} onChange={(v) => setFilters({ ...filters, min_score: v })} testid="briefs-filter-score" />
        <NumField label="Jours" value={filters.days} onChange={(v) => setFilters({ ...filters, days: v })} testid="briefs-filter-days" />
        <button
          type="button"
          onClick={() => setFilters({ direction: "", horizon: "", source_module: "", min_score: "", days: "" })}
          className="btn-ghost text-xs"
          data-testid="briefs-filter-clear"
        >
          Effacer
        </button>
      </div>

      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="noam-card p-12 text-center" data-testid="briefs-empty">
          <FileText className="w-10 h-10 text-zinc-500 mx-auto opacity-50" />
          <div className="noam-display text-base mt-3">Aucun brief archivé</div>
          <p className="text-zinc-400 text-xs mt-2 max-w-sm mx-auto">
            Chaque export PDF depuis le Radar (Impact Modal → "Exporter en note PDF") atterrit ici automatiquement.
          </p>
        </div>
      ) : (
        <div className="noam-card overflow-x-auto" data-testid="briefs-table">
          <table className="w-full text-xs">
            <thead className="border-b border-white/10">
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                <th className="text-left p-2">Titre</th>
                <th className="text-left p-2 w-20">Score</th>
                <th className="text-left p-2 w-28">Direction</th>
                <th className="text-left p-2 w-28">Horizon</th>
                <th className="text-left p-2 w-20">Module</th>
                <th className="text-left p-2 w-24">Date</th>
                <th className="text-right p-2 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const dir = DIRECTION_META[b.direction];
                const scoreColor = b.score >= 7 ? "#EF4444" : b.score >= 4 ? "#EF9F27" : "#1D9E75";
                return (
                  <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`briefs-row-${b.id}`}>
                    <td className="p-2 max-w-md">
                      <div className="text-zinc-100 font-medium truncate" title={b.title}>{b.title}</div>
                      {b.extra?.entity_name && <div className="text-[10px] text-zinc-500 truncate">{b.extra.entity_name}</div>}
                    </td>
                    <td className="p-2">
                      {typeof b.score === "number" && (
                        <span className="font-bold" style={{ color: scoreColor }}>{b.score}/10</span>
                      )}
                    </td>
                    <td className="p-2">
                      {dir && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border" style={{ borderColor: `${dir.color}50`, color: dir.color, background: `${dir.color}10` }}>
                          <dir.Icon className="w-2.5 h-2.5" /> {dir.label}
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-zinc-400">{b.horizon?.replace("_", " ") || "—"}</td>
                    <td className="p-2 text-zinc-400">{b.source_module || "—"}</td>
                    <td className="p-2 text-zinc-500 text-[10px]">{b.created_at?.slice(0, 10)}</td>
                    <td className="p-2 text-right space-x-1">
                      {/* V23.15.25 — Reprendre la conversation Intelligence V2 archivée */}
                      {b.source_module === "intelligence_v2" && b.extra?.session_id && (
                        <button
                          type="button"
                          onClick={() => reopenConversation(b)}
                          className="text-zinc-300 hover:text-[#378ADD] p-1"
                          data-testid={`briefs-reopen-${b.id}`}
                          title="Reprendre la conversation"
                        >
                          <MessageSquare className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => download(b)}
                        disabled={downloadingId === b.id}
                        className="btn-ghost text-xs disabled:opacity-50"
                        data-testid={`briefs-download-${b.id}`}
                        title="Régénérer et télécharger"
                      >
                        {downloadingId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                      </button>
                      {b.extra?.signal_url && (
                        <a
                          href={b.extra.signal_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex text-zinc-400 hover:text-[#378ADD] p-1"
                          title="Source"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(b)}
                        className="text-zinc-500 hover:text-red-400 p-1"
                        data-testid={`briefs-delete-${b.id}`}
                        title="Retirer de la bibliothèque"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="noam-card p-3">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="noam-input text-xs mt-1 block" data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</label>
      <input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="noam-input text-xs mt-1 w-20" data-testid={testid} />
    </div>
  );
}
